import { Object3D, Vector2, Vector3, Sphere } from 'three'
import QuadTree from './QuadTree'
import { qrToWorld, screenToWorld, qrToWorldX, qrToWorldY } from './coords'
import MapMesh from './MapMesh'
import { BoundingBox } from './BoundingBox'
import { range } from './util'
export default class ChunkedLazyMapMesh extends Object3D {
  constructor(tileGrid, options) {
    super()
    this.tileGrid = tileGrid
    this.options = options
    this.thunks = []
    // we're gonna handle frustrum culling ourselves
    this.frustumCulled = false
    // calculate size of map chunks so that there are at least 4 or each chunk contains 32^2 tiles
    const chunkSize = Math.min((tileGrid.width * tileGrid.height) / 4, Math.pow(32, 2))
    const chunkWidth = Math.ceil(Math.sqrt(chunkSize))
    const numChunksX = Math.ceil(tileGrid.width / chunkWidth)
    const numChunksY = Math.ceil(tileGrid.height / chunkWidth)
    const chunks = range(numChunksX).map(() => range(numChunksY).map(() => []))
    // assign tiles to cells in the coarser chunk grid
    tileGrid.forEachIJ((i, j, q, r, tile) => {
      const bx = Math.floor((i / tileGrid.width) * numChunksX)
      const by = Math.floor((j / tileGrid.height) * numChunksY)
      chunks[bx][by].push(tile)
    })
    const promises = []
    // create a thunk for each chunk
    chunks.forEach((row) => {
      row.forEach((tiles) => {
        const thunk = new MapThunk(tiles, tileGrid, options)
        this.thunks.push(thunk)
        promises.push(thunk.loaded)
        thunk.load() // preload
        this.add(thunk)
      })
    })
    this.loaded = Promise.all(promises).then(() => null)
    this.quadtree = new QuadTree(this.thunks, 1, (thunk) => thunk.computeCenter())
  }
  get numChunks() {
    return this.thunks.length
  }
  /**
   * Adjusts visibility of chunks so that only map parts that can actually be seen by the camera are rendered.
   * @param camera the camera to use for visibility checks
   */
  updateVisibility(camera) {
    const min = screenToWorld(0, 0, camera)
    const max = screenToWorld(window.innerWidth, window.innerHeight, camera)

    // If ray doesn't intersect Z plane, skip visibility update
    if (!min || !max) {
      return
    }

    const center = new Vector3().addVectors(min, max).multiplyScalar(0.5)
    const size = Math.max(max.x - min.x, max.y - min.y)
    const boundingBox = new BoundingBox(new Vector2(center.x, center.y), size * 2)
    this.thunks.forEach((thunk) => thunk.updateVisibility(false))
    this.quadtree.queryRange(boundingBox).forEach((thunk) => thunk.updateVisibility(true))
  }
  updateTiles(tiles) {
    this.thunks.forEach((thunk) => thunk.updateTiles(tiles))
  }
  getTile(q, r) {
    const xy = qrToWorld(q, r)
    const queryBounds = new BoundingBox(xy, 1)
    const thunks = this.quadtree.queryRange(queryBounds)
    for (let thunk of thunks) {
      const tile = thunk.getTile(q, r)
      if (tile) {
        return tile
      }
    }
    return null
  }
}
class MapThunk extends Object3D {
  constructor(tiles, grid, options) {
    super()
    this.tiles = tiles
    this.grid = grid
    this.options = options
    this._loaded = false
    this.loaded = new Promise((resolve) => {
      this.resolve = resolve
    })
    this.frustumCulled = false
  }
  getTiles() {
    return this.tiles
  }
  getTile(q, r) {
    return this.mesh.getTile(q, r)
  }
  computeCenter() {
    const sphere = new Sphere()
    sphere.setFromPoints(
      this.tiles.map((tile) => new Vector3(qrToWorldX(tile.q, tile.r), qrToWorldY(tile.q, tile.r)))
    )
    return new Vector2(sphere.center.x, sphere.center.y)
  }
  updateTiles(tiles) {
    if (!this.mesh) {
      this.load()
    }
    this.mesh.updateTiles(tiles)
  }
  load() {
    if (!this._loaded) {
      this._loaded = true
      const mesh = (this.mesh = new MapMesh(this.tiles, this.options, this.grid))
      mesh.frustumCulled = false
      this.add(mesh)
      this.resolve()
    }
  }
  updateVisibility(value) {
    if (value && !this._loaded) {
      this.load()
    }
    this.visible = value
  }
}
